import type {
  PackKind,
  PackTier,
  PackVersionStatus,
  PackVisibility,
} from '@planuze/admin-contracts';
import type { AdminLocale } from '../../content/locale.js';

/* Rótulos dos enums do catálogo de packs (ADR-0400/0423). Fonte única compartilhada entre a
   tabela do catálogo e a tela de detalhe — evita divergência de tradução.

   `locale` é OPCIONAL (default pt-BR) e não um `Bilingual<T>`/`useBilingual` porque este módulo
   também é importado fora desta área — `pack-submissions/submission-overview-card.tsx` e
   `routes/pack-detail-page.tsx`, ambas em frentes de migração próprias que ainda chamam estas
   funções com um único argumento. Um hook aqui quebraria essas chamadas; o parâmetro opcional
   mantém compatibilidade e permite que os consumidores desta área (que já leem `useAdminLocale`)
   passem o idioma corrente. */

const VISIBILITY_LABEL: Readonly<Record<AdminLocale, Readonly<Record<PackVisibility, string>>>> = {
  'pt-BR': { 'org-private': 'Org privada', byMyself: 'Pessoal', public: 'Pública' },
  'en-US': { 'org-private': 'Org-private', byMyself: 'Personal', public: 'Public' },
};

/* byMyself = pack pessoal da conta; org-private = restrito às orgs autorizadas. */
export function visibilityLabel(visibility: PackVisibility, locale: AdminLocale = 'pt-BR'): string {
  return VISIBILITY_LABEL[locale][visibility];
}

const TIER_LABEL: Readonly<Record<AdminLocale, Readonly<Record<PackTier, string>>>> = {
  'pt-BR': { free: 'Free', pro: 'Pro', team: 'Team', enterprise: 'Enterprise' },
  'en-US': { free: 'Free', pro: 'Pro', team: 'Team', enterprise: 'Enterprise' },
};

export function tierLabel(tier: PackTier, locale: AdminLocale = 'pt-BR'): string {
  return TIER_LABEL[locale][tier];
}

const KIND_LABEL: Readonly<Record<AdminLocale, Readonly<Record<PackKind, string>>>> = {
  'pt-BR': { template: 'Template', extension: 'Extensão', 'module-pack': 'Module pack' },
  'en-US': { template: 'Template', extension: 'Extension', 'module-pack': 'Module pack' },
};

export function kindLabel(kind: PackKind, locale: AdminLocale = 'pt-BR'): string {
  return KIND_LABEL[locale][kind];
}

/* Status de ciclo de vida de versão (version-index). `revoked` = takedown de staff (ADR-0464),
   distinto do yank/deprecate do publisher. */
const VERSION_STATUS_LABEL: Readonly<
  Record<AdminLocale, Readonly<Record<PackVersionStatus, string>>>
> = {
  'pt-BR': {
    active: 'Ativa',
    yanked: 'Yanked',
    deprecated: 'Depreciada',
    revoked: 'Removida (takedown)',
  },
  'en-US': {
    active: 'Active',
    yanked: 'Yanked',
    deprecated: 'Deprecated',
    revoked: 'Removed (takedown)',
  },
};

export function versionStatusLabel(
  status: PackVersionStatus,
  locale: AdminLocale = 'pt-BR',
): string {
  return VERSION_STATUS_LABEL[locale][status];
}

/* Variant do StatusBadge por status — `revoked` destrutivo, `active` sucesso. Não é texto,
   então independe de locale. */
const VERSION_STATUS_VARIANT: Readonly<
  Record<PackVersionStatus, 'success' | 'warning' | 'neutral' | 'destructive'>
> = {
  active: 'success',
  yanked: 'warning',
  deprecated: 'neutral',
  revoked: 'destructive',
};

export function versionStatusVariant(
  status: PackVersionStatus,
): 'success' | 'warning' | 'neutral' | 'destructive' {
  return VERSION_STATUS_VARIANT[status];
}
